import * as React from 'react';
import { styled } from '@mui/material/styles';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import classNames from 'classnames';
import './Accordion.scss';

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
  '&.MuiPaper-root': {
    boxShadow: '0px 0px 16px #0033a51a',
    marginBottom: 16,
    borderRadius: 18,
  },
}));

const expandIconStyle = {
  fontSize: '1rem',
  backgroundColor: 'black',
  color: 'white',
  borderRadius: '50%',
  fontWeight: 'bold',
  width: 20,
  height: 20,
};

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={
      props.expanded === 1 ? (
        <RemoveIcon sx={expandIconStyle} />
      ) : (
        <AddIcon sx={expandIconStyle} />
      )
    }
    {...props}
  />
))(({ theme }) => ({
  border: 'none',
  direction: 'rtl',
  alignItems: 'center',
  padding: '20px',
  '&.Mui-focusVisible': {
    outline: '2px solid #005fcc',
    outlineOffset: '2px',
    boxShadow: '0 0 0 4px rgb(0 95 204 / 25%)',
    borderRadius: '12px',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
    textAlign: 'right',
    margin: 0,
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  paddingTop: 0,
  '& .MuiTypography-root': {
    width: '90%',
    textAlign: 'right',
    fontSize: '1.125rem',
    direction: 'rtl',
    marginLeft: 'auto',
    marginRight: 5,
  },
}));

const RCAccordion = ({ items = [] }) => {
  const linkRegex = /(https?:\/\/[^\s]+)/g;
  const ACCORDION_ID_PREFIX = 'faq-accordion-panel';

  const [expanded, setExpanded] = React.useState(0);

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleLinkClick = (url) => {
    window.open(url, '_blank');
  };

  const hasUnderline = (text) => {
    return /@underline@.*@underline@/.test(text);
  };

  const removeUnderline = (text) => {
    return text.replace(/@underline@/g, '');
  };

  const renderDescription = (description) => {
    if (hasUnderline(description)) {
      description = removeUnderline(description);
      return <span style={{ textDecoration: 'underline' }}>{description}</span>;
    }

    const descriptionParts = description.split(linkRegex);
    return descriptionParts.map((part, index) => {
      if (part.match(linkRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ cursor: 'pointer' }}
            onClick={() => handleLinkClick(part)}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const getAccordionA11yProps = (index) => ({
    headerId: `${ACCORDION_ID_PREFIX}-${index + 1}-header`,
    contentId: `${ACCORDION_ID_PREFIX}-${index + 1}-content`,
  });

  return (
    <div>
      {items?.map(({ title, description }, index) => {
        const { headerId, contentId } = getAccordionA11yProps(index);

        return (
          <Accordion
            key={index}
            expanded={expanded === index}
            onChange={handleChange(index)}
          >
            <AccordionSummary
              aria-controls={contentId}
              id={headerId}
              expanded={expanded === index ? 1 : 0}
            >
              <h2
                className={classNames('accordionTitle', {
                  expanded: expanded === index,
                })}
              >
                {title}
              </h2>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                {description.split('@linebreak@').map((item, index) => (
                  <React.Fragment key={index}>
                    {renderDescription(item)}
                    <br />
                  </React.Fragment>
                ))}
              </Typography>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
};

export default RCAccordion;
